import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, BellRing, Send, ChevronDown, ChevronUp, MailOpen, MousePointerClick, Car, DollarSign, Users } from "lucide-react";
import { fmtDateTime } from "@/lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => `$${Number(v || 0).toLocaleString()}`;

function Stat({ label, value, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="bg-[#0C0C0C] border border-white/10 rounded-xl p-4">
      <div className="flex items-center gap-2 text-white/45 text-xs uppercase tracking-widest">{Icon && <Icon size={13} />}{label}</div>
      <div className="font-display font-black text-2xl mt-1.5 text-white">{value}</div>
    </div>
  );
}

export default function VehicleAlerts({ token }) {
  const H = { headers: { Authorization: `Bearer ${token}` } };
  const [d, setD] = useState(null);
  const [sending, setSending] = useState(null);
  const [open, setOpen] = useState(null);

  const load = () => axios.get(`${API}/vehicle-alerts`, H).then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const notify = async (v) => {
    if (!window.confirm(`Send an availability alert for the ${v.vehicle} to ${v.candidates} waiting customer(s)?`)) return;
    setSending(v.id);
    try {
      const { data } = await axios.post(`${API}/vehicle-alerts/notify`, { vehicle_id: v.id }, H);
      if (data.notified > 0) toast.success(`Alert sent to ${data.notified} customer(s) for the ${v.vehicle}.`);
      else toast.info(data.reason || "No eligible customers to notify right now.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send alerts."); }
    finally { setSending(null); }
  };

  if (!d) return <div className="flex items-center gap-2 text-white/50 py-16 justify-center"><Loader2 className="animate-spin" size={18} /> Loading vehicle alerts…</div>;

  return (
    <div className="space-y-5" data-testid="vehicle-alerts">
      <div>
        <h3 className="font-display font-bold text-white flex items-center gap-2"><BellRing size={17} className="text-[#F2B705]" /> Available Vehicle Alerts</h3>
        <p className="text-white/45 text-sm mt-0.5">When a car flips back to Available, unbooked requesters and recent leads from the last 30 days get an SMS + email with a one-click booking link. Customers who open but don't click get one gentle follow-up nudge after 24 hours. Alerts stop as soon as the vehicle books — no duplicates.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat testid="va-notified" label="Customers Notified" value={d.totals.notified} icon={Users} />
        <Stat testid="va-opened" label="Emails Opened" value={d.totals.opened} icon={MailOpen} />
        <Stat testid="va-clicked" label="Links Clicked" value={d.totals.clicked} icon={MousePointerClick} />
        <Stat testid="va-booked" label="Bookings" value={d.totals.booked} icon={Car} />
        <Stat testid="va-revenue" label="Revenue From Alerts" value={money(d.totals.revenue)} icon={DollarSign} />
      </div>

      {/* Manual notify */}
      <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5" data-testid="va-manual">
        <h4 className="text-sm font-semibold text-white/80 mb-3">Send Alert Now — manually notify waiting customers</h4>
        <div className="space-y-2">
          {d.vehicles.filter((v) => v.status === "available").map((v) => (
            <div key={v.id} className="flex items-center justify-between gap-3 bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2.5">
              <div className="min-w-0">
                <span className="text-white/90 text-sm font-semibold">{v.vehicle}</span>
                <span className="text-white/40 text-xs ml-2">{v.candidates} waiting customer(s){v.matched_by === "dates" ? " · date-matched" : ""}</span>
              </div>
              <button data-testid={`va-notify-${v.id}`} onClick={() => notify(v)} disabled={sending === v.id || !v.candidates}
                className="flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-md bg-[#F2B705] text-black hover:bg-white transition-colors disabled:opacity-40 shrink-0">
                {sending === v.id ? <Loader2 className="animate-spin" size={13} /> : <Send size={13} />} Send Alert Now
              </button>
            </div>
          ))}
          {d.vehicles.filter((v) => v.status === "available").length === 0 && <p className="text-white/40 text-sm">No vehicles are available right now — every car is out. 🚗</p>}
        </div>
      </div>

      {/* Alert history */}
      <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5" data-testid="va-events">
        <h4 className="text-sm font-semibold text-white/80 mb-3">Alert History</h4>
        {d.events.length === 0 && <p className="text-white/40 text-sm">No alerts sent yet. They'll fire automatically the next time a rented or maintenance vehicle becomes available.</p>}
        <div className="space-y-2">
          {d.events.map((e) => (
            <div key={e.id} data-testid={`va-event-${e.id}`} className="bg-[#0A0A0A] border border-white/10 rounded-lg">
              <button onClick={() => setOpen(open === e.id ? null : e.id)} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left">
                <div className="min-w-0">
                  <span className="text-white/90 text-sm font-semibold">{e.vehicle}</span>
                  <span className={`ml-2 text-[10px] uppercase px-1.5 py-0.5 rounded ${e.trigger === "manual" ? "bg-sky-500/15 text-sky-300" : "bg-white/5 text-white/50"}`}>{e.trigger}</span>
                  <span className={`ml-1.5 text-[10px] uppercase px-1.5 py-0.5 rounded ${e.status === "active" ? "bg-emerald-500/15 text-emerald-300" : "bg-white/5 text-white/40"}`}>{e.status === "active" ? "active" : "closed — booked"}</span>
                  <div className="text-white/35 text-xs mt-0.5">{fmtDateTime(e.created_at)} · matched by {e.matched_by === "dates" ? "requested dates" : "recent leads"}</div>
                </div>
                <div className="flex items-center gap-4 text-xs text-white/60 shrink-0">
                  <span title="notified"><Users size={12} className="inline mr-1" />{e.notified}</span>
                  <span title="opened"><MailOpen size={12} className="inline mr-1" />{e.opened}</span>
                  <span title="clicked"><MousePointerClick size={12} className="inline mr-1" />{e.clicked}</span>
                  <span title="booked" className="text-emerald-400"><Car size={12} className="inline mr-1" />{e.booked}</span>
                  {e.revenue > 0 && <span className="text-emerald-400 font-bold">{money(e.revenue)}</span>}
                  {open === e.id ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                </div>
              </button>
              {open === e.id && (
                <div className="border-t border-white/5 px-4 py-3 space-y-1.5" data-testid={`va-recipients-${e.id}`}>
                  {e.recipients.map((r, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 text-xs">
                      <span className="text-white/80 truncate">{r.name} <span className="text-white/35">{r.email || r.phone} · {r.source}</span></span>
                      <span className="flex items-center gap-2 shrink-0">
                        {r.sms_sent && <span className="text-white/50">SMS ✓</span>}
                        {r.email_sent && <span className="text-white/50">Email ✓</span>}
                        {r.opened_at && <span className="text-sky-300">Opened</span>}
                        {r.nudged_at && <span className="text-amber-300">Nudged</span>}
                        {r.clicked_at && <span className="text-[#F2B705]">Clicked</span>}
                        {r.booked_booking_id && <span className="text-emerald-400 font-bold">BOOKED {money(r.revenue)}</span>}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
